import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../services/api';
import { cn } from '../../utils/cn';

interface Step {
  target: string; // CSS selector
  titleKey: string;
  descKey: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const onboardingSteps: Step[] = [
  { target: '[data-onboarding="dashboard"]', titleKey: 'onboarding.dashboard', descKey: 'onboarding.dashboardDesc', position: 'bottom' },
  { target: '[data-onboarding="projects"]', titleKey: 'onboarding.projects', descKey: 'onboarding.projectsDesc', position: 'right' },
  { target: '[data-onboarding="my-tasks"]', titleKey: 'onboarding.myTasks', descKey: 'onboarding.myTasksDesc', position: 'right' },
  { target: '[data-onboarding="gantt"]', titleKey: 'onboarding.gantt', descKey: 'onboarding.ganttDesc', position: 'right' },
  { target: '[data-onboarding="notifications"]', titleKey: 'onboarding.notifications', descKey: 'onboarding.notificationsDesc', position: 'right' },
  { target: '[data-onboarding="settings"]', titleKey: 'onboarding.settings', descKey: 'onboarding.settingsDesc', position: 'right' },
];

export function OnboardingOverlay() {
  const { user, fetchMe } = useAuthStore();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.is_onboarding) {
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [user?.is_onboarding]);

  useEffect(() => {
    if (!visible) return;
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [visible, currentStep]);

  function positionTooltip() {
    const step = onboardingSteps[currentStep];
    const el = document.querySelector(step.target);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const style: React.CSSProperties = { position: 'fixed', zIndex: 1000 };

    switch (step.position) {
      case 'bottom':
        style.top = rect.bottom + 12;
        style.left = rect.left;
        break;
      case 'right':
        style.top = rect.top;
        style.left = rect.right + 12;
        break;
      case 'left':
        style.top = rect.top;
        style.right = window.innerWidth - rect.left + 12;
        break;
      case 'top':
        style.bottom = window.innerHeight - rect.top + 12;
        style.left = rect.left;
        break;
    }

    setTooltipStyle(style);

    // Highlight the target element
    el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'relative', 'z-[999]');
    return () => {
      el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'relative', 'z-[999]');
    };
  }

  async function handleClose() {
    // Remove highlights
    onboardingSteps.forEach(step => {
      const el = document.querySelector(step.target);
      el?.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'relative', 'z-[999]');
    });

    setVisible(false);
    if (user) {
      await api.updateUserSettings(user.id, { is_onboarding: false });
      await fetchMe();
    }
  }

  function goNext() {
    // Remove current highlight
    const el = document.querySelector(onboardingSteps[currentStep].target);
    el?.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'relative', 'z-[999]');

    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  }

  function goPrev() {
    const el = document.querySelector(onboardingSteps[currentStep].target);
    el?.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'relative', 'z-[999]');

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  if (!visible) return null;

  const step = onboardingSteps[currentStep];
  const isLast = currentStep === onboardingSteps.length - 1;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-[998]" />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-5 w-80 z-[1000] animate-in fade-in"
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-bold text-sm dark:text-gray-100">{t(step.titleKey)}</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 -mt-1 -mr-1" aria-label={t('common.close')}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t(step.descKey)}</p>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {onboardingSteps.map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-2 h-2 rounded-full transition-colors',
                i === currentStep ? 'bg-blue-600' : i < currentStep ? 'bg-blue-300' : 'bg-gray-200 dark:bg-gray-600'
              )}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={goPrev}
            disabled={currentStep === 0}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
          >
            <ChevronLeft className="w-3 h-3" /> {t('common.prev')}
          </button>
          <span className="text-xs text-gray-400 dark:text-gray-500">{currentStep + 1} / {onboardingSteps.length}</span>
          <button
            onClick={goNext}
            className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
          >
            {isLast ? (
              <>{t('common.done')} <CheckCircle2 className="w-3 h-3" /></>
            ) : (
              <>{t('common.next')} <ChevronRight className="w-3 h-3" /></>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
